/**
 * 門標註 Canvas
 *
 * 提供互動式介面讓使用者標註門的位置：
 * - 繪製門（拖曳線條）
 * - 自動對齊到水平/垂直
 * - 雙擊刪除門
 * - 支援多種門類型（主要入口、一般門、緊急出口）
 */

import { BaseCanvas, type CanvasOptions } from './base-canvas';
import { drawLine, fillCircle, drawText } from './drawing-utils';
import { DOOR } from '@/config/constants';
import { distance, radiansToDegrees, normalizeAngle } from '@/utils/geometry';
import { plannerStore, setDoors } from '@/store/planner-store';
import type { Point, Door, DoorType } from '@/types';

// ========================================================================
// 型別定義
// ========================================================================

/** 門標註 Canvas 選項 */
export interface DoorCanvasOptions extends Omit<CanvasOptions, 'canvas'> {
  canvas: HTMLCanvasElement | string;
  /** 取得當前門類型的回呼函式 */
  getDoorType?: () => DoorType;
  /** 門類型選擇器元素 ID（已棄用，請使用 getDoorType） */
  typeSelectId?: string;
  /** 門列表元素 ID */
  doorListId?: string;
  /** 門數量顯示元素 ID */
  doorCountId?: string;
}

/** 內部門資料（包含繪製用的額外資訊） */
interface InternalDoor extends Door {
  id: number;
  x: number;
  y: number;
  angle: number;
  thickness: number;
}

// ========================================================================
// DoorCanvas 類別
// ========================================================================

export class DoorCanvas extends BaseCanvas {
  /** 內部門列表 */
  private internalDoors: InternalDoor[] = [];

  /** 繪製起點（兩次點擊模式：有值表示正在繪製） */
  private startPoint: Point | null = null;

  /** 當前滑鼠位置（用於預覽） */
  private currentPoint: Point | null = null;

  /** 防止 click 事件刪除剛新增的門 */
  private justAdded: boolean = false;

  /** 取得當前門類型的回呼函式 */
  private getDoorType?: () => DoorType;

  /** 門類型選擇器（已棄用） */
  private typeSelect: HTMLSelectElement | null = null;

  /** 門列表元素 */
  private doorListElement: HTMLElement | null = null;

  /** 門數量顯示元素 */
  private doorCountElement: HTMLElement | null = null;

  /** Store 取消訂閱函式 */
  private unsubscribe: (() => void) | null = null;

  /** 鍵盤事件處理函式（用於取消訂閱） */
  private keydownHandler: ((e: KeyboardEvent) => void) | null = null;

  constructor(options: DoorCanvasOptions) {
    super({ ...options, cursor: 'crosshair' });

    // 設定門類型取得方式
    this.getDoorType = options.getDoorType;

    // 取得 UI 元素（舊方式，保留向後相容）
    if (options.typeSelectId) {
      this.typeSelect = document.getElementById(options.typeSelectId) as HTMLSelectElement;
    }
    if (options.doorListId) {
      this.doorListElement = document.getElementById(options.doorListId);
    }
    if (options.doorCountId) {
      this.doorCountElement = document.getElementById(options.doorCountId);
    }
  }

  // ========================================================================
  // 初始化
  // ========================================================================

  initialize(): void {
    this.bindPointerEvents({
      onPointerDown: this.onPointerDown.bind(this),
      onPointerMove: this.onPointerMove.bind(this),
      onPointerUp: this.onPointerUp.bind(this),
      onDoubleClick: this.onDoubleClick.bind(this),
    });

    // 訂閱 Store 變更
    this.unsubscribe = plannerStore.subscribeSelector(
      (state) => state.doors,
      (doors) => {
        this.syncFromStore(doors);
        this.render();
      }
    );

    // 從 Store 同步初始資料
    this.syncFromStore(plannerStore.getState().doors);

    // 監聽 Escape 鍵取消正在進行的繪製
    this.keydownHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && this.startPoint) {
        this.startPoint = null;
        this.currentPoint = null;
        this.render();
      }
    };
    window.addEventListener('keydown', this.keydownHandler);
  }

  // ========================================================================
  // Store 同步
  // ========================================================================

  /**
   * 從 Store 同步門資料
   */
  private syncFromStore(doors: Door[]): void {
    this.internalDoors = doors.map((door, index) => ({
      ...door,
      id: Date.now() + index,
      x: (door.startX + door.endX) / 2,
      y: (door.startY + door.endY) / 2,
      angle: Math.atan2(door.endY - door.startY, door.endX - door.startX),
      thickness: 10,
    }));
    this.updateDoorList();
  }

  /**
   * 同步門資料到 Store
   */
  private syncToStore(): void {
    const doors: Door[] = this.internalDoors.map((door) => ({
      type: door.type,
      startX: door.startX,
      startY: door.startY,
      endX: door.endX,
      endY: door.endY,
      width: door.width,
    }));
    setDoors(doors);
  }

  // ========================================================================
  // 事件處理
  // ========================================================================

  private onPointerDown(point: Point, _event: PointerEvent): void {
    // 使用兩次點擊的方式繪製門
    if (!this.startPoint) {
      // 第一次點擊：設定起點
      this.startPoint = point;
      this.currentPoint = point;
      this.render();
    } else {
      // 第二次點擊：完成門的繪製
      const length = distance(this.startPoint, point);

      // 最小長度閾值
      if (length > DOOR.MIN_LENGTH) {
        const door = this.createDoor(this.startPoint, point);
        this.internalDoors.push(door);
        this.justAdded = true;
        this.syncToStore();
        this.updateDoorList();

        setTimeout(() => {
          this.justAdded = false;
        }, 200);
      }

      this.startPoint = null;
      this.currentPoint = null;
      this.render();
    }
  }

  private onPointerMove(point: Point, _event: PointerEvent): void {
    // 有起點時即時更新預覽
    if (!this.startPoint) return;
    this.currentPoint = point;
    this.render();
  }

  private onPointerUp(_point: Point, _event: PointerEvent): void {
    // 兩次點擊模式不需要在 pointerUp 處理
  }

  private onDoubleClick(point: Point, _event: PointerEvent): void {
    if (this.justAdded) return;

    // 檢查是否雙擊到某個門
    for (let i = this.internalDoors.length - 1; i >= 0; i--) {
      const door = this.internalDoors[i];
      if (!door) continue;

      const dist = this.pointToLineDistance(point, door);
      if (dist < DOOR.HIT_THRESHOLD) {
        this.internalDoors.splice(i, 1);
        this.syncToStore();
        this.updateDoorList();
        this.render();
        break;
      }
    }
  }

  // ========================================================================
  // 門建立
  // ========================================================================

  /**
   * 建立門物件（包含自動對齊）
   */
  private createDoor(start: Point, end: Point): InternalDoor {
    // 計算角度
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const angleDegrees = radiansToDegrees(Math.atan2(dy, dx));

    // 決定對齊到水平或垂直
    const angleModulo = normalizeAngle(angleDegrees) % 180;
    const deviationFromHorizontal = Math.min(angleModulo, 180 - angleModulo);
    const deviationFromVertical = Math.abs(90 - angleModulo);

    let alignedStart: Point;
    let alignedEnd: Point;

    if (deviationFromHorizontal < deviationFromVertical) {
      // 對齊到水平線
      const avgY = (start.y + end.y) / 2;
      alignedStart = { x: start.x, y: avgY };
      alignedEnd = { x: end.x, y: avgY };
    } else {
      // 對齊到垂直線
      const avgX = (start.x + end.x) / 2;
      alignedStart = { x: avgX, y: start.y };
      alignedEnd = { x: avgX, y: end.y };
    }

    const alignedLength = distance(alignedStart, alignedEnd);
    const alignedAngle = Math.atan2(alignedEnd.y - alignedStart.y, alignedEnd.x - alignedStart.x);

    // 優先使用 callback，其次是 select 元素，最後預設為 'normal'
    const doorType = this.getDoorType?.() ?? (this.typeSelect?.value as DoorType) ?? 'normal';

    return {
      id: Date.now(),
      type: doorType,
      startX: alignedStart.x,
      startY: alignedStart.y,
      endX: alignedEnd.x,
      endY: alignedEnd.y,
      width: alignedLength,
      x: (alignedStart.x + alignedEnd.x) / 2,
      y: (alignedStart.y + alignedEnd.y) / 2,
      angle: alignedAngle,
      thickness: 10,
    };
  }

  /**
   * 計算點到線段的距離
   */
  private pointToLineDistance(point: Point, door: InternalDoor): number {
    const dx = door.endX - door.startX;
    const dy = door.endY - door.startY;
    const lineLen = Math.sqrt(dx * dx + dy * dy);

    if (lineLen === 0) return distance(point, { x: door.startX, y: door.startY });

    const t = Math.max(
      0,
      Math.min(
        1,
        ((point.x - door.startX) * dx + (point.y - door.startY) * dy) / (lineLen * lineLen)
      )
    );

    const projX = door.startX + t * dx;
    const projY = door.startY + t * dy;

    return distance(point, { x: projX, y: projY });
  }

  // ========================================================================
  // 渲染
  // ========================================================================

  render(): void {
    this.clear();
    this.drawImage();

    this.beginImageCoords();

    // 繪製所有門
    for (const door of this.internalDoors) {
      this.drawDoor(door);
    }

    // 繪製預覽（兩次點擊模式：有起點時就顯示預覽）
    if (this.startPoint && this.currentPoint) {
      this.drawPreviewLine(this.startPoint, this.currentPoint);
    }

    this.endImageCoords();
  }

  /**
   * 繪製單一門
   */
  private drawDoor(door: InternalDoor): void {
    const color = DOOR.COLORS[door.type] ?? DOOR.COLORS.normal;
    const lineWidth = DOOR.LINE_WIDTH / this.scale;
    const dotRadius = 5 / this.scale;

    // 繪製門線條
    drawLine(
      this.ctx,
      { x: door.startX, y: door.startY },
      { x: door.endX, y: door.endY },
      { color, width: lineWidth }
    );

    // 繪製兩端圓點
    fillCircle(this.ctx, { x: door.startX, y: door.startY }, dotRadius, { color });
    fillCircle(this.ctx, { x: door.endX, y: door.endY }, dotRadius, { color });

    // 繪製門編號
    const index = this.internalDoors.indexOf(door) + 1;
    drawText(
      this.ctx,
      index.toString(),
      { x: door.x, y: door.y - 15 / this.scale },
      {
        color,
        size: 14 / this.scale,
        bold: true,
        align: 'center',
      }
    );
  }

  /**
   * 繪製預覽線條
   */
  private drawPreviewLine(start: Point, end: Point): void {
    const color = 'rgba(59, 130, 246, 0.8)';
    const lineWidth = 4 / this.scale;
    const dotRadius = 6 / this.scale;

    // 繪製虛線
    drawLine(this.ctx, start, end, {
      color,
      width: lineWidth,
      dash: [10 / this.scale, 5 / this.scale],
    });

    // 繪製起點標記（綠色表示已確定）
    fillCircle(this.ctx, start, dotRadius, { color: '#22c55e' });
    // 繪製終點標記（藍色表示待確定）
    fillCircle(this.ctx, end, dotRadius, { color });
  }

  // ========================================================================
  // UI 更新
  // ========================================================================

  /**
   * 更新門列表顯示
   * 🔒 安全性：使用 DOM API 而非 innerHTML 模板字串
   */
  private updateDoorList(): void {
    if (this.doorCountElement) {
      this.doorCountElement.textContent = String(this.internalDoors.length);
    }

    if (!this.doorListElement) return;

    // 清空現有內容
    this.doorListElement.innerHTML = '';

    if (this.internalDoors.length === 0) {
      const emptyMessage = document.createElement('p');
      emptyMessage.className = 'text-sm text-gray-400';
      emptyMessage.textContent = '尚未標註任何門';
      this.doorListElement.appendChild(emptyMessage);
      return;
    }

    const typeNames: Record<DoorType, string> = {
      main: '主要入口',
      normal: '普通門',
      emergency: '緊急出口',
    };

    // 使用 DOM API 建構列表
    const container = document.createElement('div');
    container.className = 'space-y-1';

    for (let i = 0; i < this.internalDoors.length; i++) {
      const door = this.internalDoors[i];
      if (!door) continue;

      const doorItem = document.createElement('div');
      doorItem.className = 'text-xs p-2 bg-gray-50 rounded border border-gray-200';

      const titleDiv = document.createElement('div');
      titleDiv.className = 'font-semibold';
      titleDiv.textContent = `門 ${i + 1}: ${typeNames[door.type]}`;

      const widthDiv = document.createElement('div');
      widthDiv.className = 'text-gray-600';
      widthDiv.textContent = `寬度: ${Math.round(door.width)}px`;

      doorItem.appendChild(titleDiv);
      doorItem.appendChild(widthDiv);
      container.appendChild(doorItem);
    }

    this.doorListElement.appendChild(container);
  }

  // ========================================================================
  // 公開方法
  // ========================================================================

  /**
   * 清除所有門
   */
  clearAll(): void {
    this.internalDoors = [];
    this.syncToStore();
    this.updateDoorList();
    this.render();
  }

  /**
   * 取得門列表
   */
  getDoors(): Door[] {
    return this.internalDoors.map((door) => ({
      type: door.type,
      startX: door.startX,
      startY: door.startY,
      endX: door.endX,
      endY: door.endY,
      width: door.width,
    }));
  }

  /**
   * 設定門列表
   */
  setDoors(doors: Door[]): void {
    setDoors(doors);
  }

  /**
   * 產生包含門標註的圖片
   */
  generateImageWithDoors(): string {
    if (!this.image) return '';

    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = this.image.width;
    tempCanvas.height = this.image.height;
    const tempCtx = tempCanvas.getContext('2d');
    if (!tempCtx) return '';

    // 繪製原始圖片
    tempCtx.drawImage(this.image, 0, 0);

    // 繪製門（黑色粗線，便於辨識）
    for (const door of this.internalDoors) {
      tempCtx.strokeStyle = '#000000';
      tempCtx.lineWidth = 8;
      tempCtx.lineCap = 'round';

      tempCtx.beginPath();
      tempCtx.moveTo(door.startX, door.startY);
      tempCtx.lineTo(door.endX, door.endY);
      tempCtx.stroke();

      tempCtx.fillStyle = '#000000';
      tempCtx.beginPath();
      tempCtx.arc(door.startX, door.startY, 4, 0, Math.PI * 2);
      tempCtx.fill();
      tempCtx.beginPath();
      tempCtx.arc(door.endX, door.endY, 4, 0, Math.PI * 2);
      tempCtx.fill();
    }

    return tempCanvas.toDataURL('image/png');
  }

  // ========================================================================
  // 生命週期
  // ========================================================================

  destroy(): void {
    this.unsubscribe?.();
    if (this.keydownHandler) {
      window.removeEventListener('keydown', this.keydownHandler);
      this.keydownHandler = null;
    }
    super.destroy();
  }
}
